import React, { useEffect, useState } from 'react';
import { analyticsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Users, UserCheck, UserPlus, AlertCircle, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';

const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#F97316', '#84CC16', '#14B8A6'];

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await analyticsAPI.overview();
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load analytics');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (error) return <div className="text-red-600 text-center py-12">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><TrendingUp />Workforce Analytics</h1>
        <p className="text-muted-foreground">Headcount, leave/expense trends and payroll cost overview</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><Users className="text-blue-600" /><div><p className="text-xs text-muted-foreground">Headcount</p><p className="text-2xl font-bold">{data.headcount?.total ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><UserCheck className="text-emerald-600" /><div><p className="text-xs text-muted-foreground">Active</p><p className="text-2xl font-bold">{data.headcount?.active ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><UserPlus className="text-amber-600" /><div><p className="text-xs text-muted-foreground">Onboarding</p><p className="text-2xl font-bold">{data.headcount?.onboarding ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><AlertCircle className="text-red-600" /><div><p className="text-xs text-muted-foreground">Approvals Pending</p><p className="text-2xl font-bold">{(data.pending?.leaves ?? 0) + (data.pending?.expenses ?? 0) + (data.pending?.onboarding ?? 0)}</p></div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Headcount by country */}
        <Card>
          <CardHeader><CardTitle className="text-base">Headcount by Country</CardTitle></CardHeader>
          <CardContent>
            {data.by_country?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_country} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#4F46E5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data</p>}
          </CardContent>
        </Card>

        {/* Headcount by status (attrition view) */}
        <Card>
          <CardHeader><CardTitle className="text-base">Workforce Status (Attrition View)</CardTitle></CardHeader>
          <CardContent>
            {data.by_status?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data.by_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(e) => `${e.name}: ${e.value}`} fontSize={11}>
                    {data.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data</p>}
          </CardContent>
        </Card>

        {/* Leave & expense trend */}
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Leave & Expense Trends (last 6 months)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={(data.leaves_trend || []).map((l, i) => ({
                month: l.month,
                Leaves: l.count,
                Expenses: data.expenses_trend?.[i]?.count ?? 0,
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Leaves" stroke="#4F46E5" strokeWidth={2} />
                <Line type="monotone" dataKey="Expenses" stroke="#F59E0B" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* By department */}
        <Card>
          <CardHeader><CardTitle className="text-base">Top Departments</CardTitle></CardHeader>
          <CardContent>
            {data.by_department?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.by_department}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={10} angle={-25} textAnchor="end" height={70} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data</p>}
          </CardContent>
        </Card>

        {/* By company */}
        <Card>
          <CardHeader><CardTitle className="text-base">Headcount by Company</CardTitle></CardHeader>
          <CardContent>
            {data.by_company?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data.by_company} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(e) => `${e.value}`} fontSize={11}>
                    {data.by_company.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-10">No data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Payroll cost */}
      {data.payroll_cost?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Payroll Cost (latest period)</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {data.payroll_cost.map((p) => (
                <div key={p.currency} className="border rounded-lg p-3 min-w-[140px]">
                  <p className="text-xs text-muted-foreground">{p.currency}</p>
                  <p className="text-xl font-bold">{p.total.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending */}
      <Card>
        <CardHeader><CardTitle className="text-base">Approval Queues</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-sm py-1.5 px-3">Leaves: {data.pending?.leaves ?? 0}</Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">Expenses: {data.pending?.expenses ?? 0}</Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">Onboarding: {data.pending?.onboarding ?? 0}</Badge>
            <Badge variant="outline" className="text-sm py-1.5 px-3">Tickets: {data.pending?.tickets ?? 0}</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
